import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import type {SortDTO} from "crqlar-react-common/dist/models/common/limit";
import {amountCentsOf} from "./serviceAutofill";

const timeOf = (date?: Date) => (date ? new Date(date).getTime() : 0);

/** Oldest first - the order the list shows them in and numbers the unnamed ones by ("Folio 2"). */
export const sortFoliosByDate = (folios: Folio[]): Folio[] => [...folios].sort((a, b) => (
    timeOf(a.insertDate) - timeOf(b.insertDate) || (a.id ?? '').localeCompare(b.id ?? '')
));

export interface FolioPosition {
    number: number
    count: number
}

/** The folio's place among the reservation's folios; undefined when it isn't among them. */
export const folioPosition = (folios: Folio[], folioId?: string): FolioPosition | undefined => {
    const index = folioId ? folios.findIndex(folio => folio.id === folioId) : -1;
    return index < 0 ? undefined : {number: index + 1, count: folios.length};
};

/** The folio's stored total (kept up to date by the backend on every line change). */
export const folioTotalCents = (folio: Folio): number => amountCentsOf(folio.total) ?? 0;

/** A row of the dialog's folio list: the folio with what the list shows of it. */
export interface FolioListEntry {
    folio: Folio
    /** Its place among the reservation's folios, oldest first. */
    number: number
    /** Its own name, else "Folio 2" by its place. */
    name: string
    /** Its status as the list shows it: an open folio is "Finalized". */
    statusLabel: string
}

export interface FolioListLabels {
    name: (folio: Folio, number: number) => string
    status: (status?: FolioStatus) => string
}

// Names and statuses as they read: "Folio 2" before "Folio 10", in any case
const TEXT_ORDER = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});

type EntryOrder = (a: FolioListEntry, b: FolioListEntry) => number;

// By the list's column ids
const ORDERS: Record<string, EntryOrder> = {
    name: (a, b) => TEXT_ORDER.compare(a.name, b.name),
    status: (a, b) => TEXT_ORDER.compare(a.statusLabel, b.statusLabel),
    total: (a, b) => folioTotalCents(a.folio) - folioTotalCents(b.folio),
    // Character by character, like the backend: an ObjectId's order is its creation's
    id: (a, b) => {
        const [left, right] = [a.folio.id ?? '', b.folio.id ?? ''];
        return left < right ? -1 : left > right ? 1 : 0;
    },
    updateDate: (a, b) => timeOf(a.folio.updateDate) - timeOf(b.folio.updateDate),
};

/**
 * The reservation's folios (all of them, oldest first) as the dialog's list shows them: those whose
 * name, id or status as shown contains the search, in any case, sorted by the chosen column - ties,
 * and no sort at all, oldest first. They are few, so it happens here: the backend's search is a
 * guest's name across all reservations.
 */
export const listFolioEntries = (folios: Folio[], labels: FolioListLabels, search: string, sort?: SortDTO): FolioListEntry[] => {
    const needle = search.trim().toLowerCase();
    const order = sort && ORDERS[sort.field];
    const direction = sort?.direction === 'DESC' ? -1 : 1;

    return folios
        .map((folio, index) => ({folio, number: index + 1, name: labels.name(folio, index + 1), statusLabel: labels.status(folio.status)}))
        .filter(entry => !needle || [entry.name, entry.folio.id ?? '', entry.statusLabel].some(text => text.toLowerCase().includes(needle)))
        .sort((a, b) => (order ? direction * order(a, b) : 0) || a.number - b.number);
};
